import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { MapPin, Mail, CheckCircle, AlertCircle } from 'lucide-react';
import { useTranslate } from '../hooks/useTranslate';
import { useLanguage } from '../context/LanguageContext';
import { OptimizedImage } from '../components/ui/optimized-image';

const Contact = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const { currentLanguage } = useLanguage();
  
  // CAPTCHA simple
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0 });
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  
  // Générer une nouvelle question CAPTCHA
  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2 });
    setCaptchaAnswer('');
  };
  
  useEffect(() => {
    generateCaptcha(); // Générer CAPTCHA au chargement
  }, []);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  // Traductions
  const { translatedText: titleTranslated } = useTranslate("Contact");
  const { translatedText: subtitleTranslated } = useTranslate("Nous sommes à votre disposition pour toute question ou demande d'information");
  const { translatedText: formTitleTranslated } = useTranslate("Nous contacter");
  const { translatedText: firstNameTranslated } = useTranslate("Prénom");
  const { translatedText: lastNameTranslated } = useTranslate("Nom");
  const { translatedText: emailTranslated } = useTranslate("Email");
  const { translatedText: subjectTranslated } = useTranslate("Sujet");
  const { translatedText: messageTranslated } = useTranslate("Message");
  const { translatedText: sendButtonTranslated } = useTranslate("Envoyer le message");
  const { translatedText: sendingTranslated } = useTranslate("Envoi en cours...");
  const { translatedText: successTitleTranslated } = useTranslate("Message envoyé avec succès !");
  const { translatedText: successMessageTranslated } = useTranslate("Votre message a été envoyé avec succès. Nous vous répondrons dans les plus brefs délais.");
  const { translatedText: backHomeTranslated } = useTranslate("Retour à l'accueil");
  const { translatedText: infoTitleTranslated } = useTranslate("Informations de contact");
  const { translatedText: headquartersTranslated } = useTranslate("Siège social");
  const { translatedText: secretariatTranslated } = useTranslate("Secrétariat");
  const { translatedText: postalAddressTranslated } = useTranslate("Adresse postale:");
  const { translatedText: becomeMemberTranslated } = useTranslate("Devenir membre");
  const { translatedText: interestedTranslated } = useTranslate("Intéressé par rejoindre l'Association Royale des Descendants des Lignages de Bruxelles ?");
  const { translatedText: learnMoreTranslated } = useTranslate("En savoir plus");
  const { translatedText: patronageTranslated } = useTranslate("Sous le haut patronage de Sa Majesté le Roi");
  const { translatedText: patronageDescTranslated } = useTranslate("L'Association Royale des Descendants des Lignages de Bruxelles est honorée de bénéficier du haut patronage de Sa Majesté le Roi, témoignage de l'importance historique et culturelle de notre patrimoine bruxellois.");
  const { translatedText: captchaLabelTranslated } = useTranslate("Sécurité (anti-spam)");
  const { translatedText: captchaErrorTranslated } = useTranslate("Veuillez résoudre le calcul de sécurité");
  const { translatedText: captchaWrongTranslated } = useTranslate("Réponse incorrecte. Veuillez réessayer.");

  useEffect(() => {
    fetchPageData();
  }, []);

  const fetchPageData = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/site/pages/contact`);
      if (response.ok) {
        const data = await response.json();
        setPageData(data);
      }
    } catch (error) {
      console.error('Erreur chargement page:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    // Valider le CAPTCHA
    const expectedAnswer = captchaQuestion.num1 + captchaQuestion.num2;
    if (parseInt(captchaAnswer) !== expectedAnswer) {
      setError(captchaWrongTranslated);
      setSubmitting(false);
      generateCaptcha(); // Générer nouveau CAPTCHA
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('subject', formData.subject);
      formDataToSend.append('message', formData.message);

      const response = await fetch(`${API_BASE}/api/contact/submit`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitted(true);
        window.scrollTo(0, 0);
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Erreur lors de l\'envoi');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Image Banner */}
      {pageData?.header_image && (
        <div 
          className="relative w-full overflow-hidden"
          style={{ 
            height: '600px',
            marginTop: '-80px',
            paddingTop: '80px'
          }}
        >
          <OptimizedImage 
            src={pageData.header_image.startsWith('/api/files/') || pageData.header_image.startsWith('http') ? (pageData.header_image.startsWith('http') ? pageData.header_image : `${API_BASE}${pageData.header_image}`) : `${API_BASE}/api/files/${pageData.header_image}`}
            alt="Contact"
            className="absolute inset-0 w-full h-full object-cover"
            priority={true}
            placeholder="blur"
          />
          <div className="absolute inset-0 bg-black bg-opacity-30"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ paddingTop: '100px' }}>
            <h1 
              className="text-5xl font-bold text-center px-4"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)',
                textShadow: '3px 3px 6px rgba(0,0,0,0.8), -1px -1px 2px rgba(255,255,255,0.3)'
              }}
            >
              {titleTranslated}
            </h1>
            <p 
              className="text-2xl text-center px-4 mt-3"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'white',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
              }}
            >
              {subtitleTranslated}
            </p>
          </div>
        </div>
      )}

      {/* Message de succès OU Formulaire */}
      {submitted ? (
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4" style={{ color: 'rgb(196, 150, 96)', fontFamily: '"Lora", serif' }}>
              {successTitleTranslated}
            </h2>
            <p className="text-gray-700 mb-4" style={{ fontFamily: '"Lora", serif' }}>
              {successMessageTranslated}
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="mt-4 px-6 py-2 text-white rounded-lg"
              style={{ backgroundColor: 'rgb(196, 150, 96)' }}
            >
              {backHomeTranslated}
            </button>
          </div>
        </div>
      ) : (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle 
                className="text-3xl"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {formTitleTranslated}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600 mt-0.5" />
                  <p className="text-red-800">{error}</p>
                </div>
              )}
              
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label 
                      htmlFor="firstName"
                      className="text-lg"
                      style={{ fontFamily: '"Lora", serif' }}
                    >
                      {firstNameTranslated}
                    </Label>
                    <Input 
                      id="firstName" 
                      placeholder="Votre prénom" 
                      className="mt-1 text-lg p-3" 
                      style={{ fontFamily: '"Lora", serif' }}
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div>
                    <Label 
                      htmlFor="lastName"
                      className="text-lg"
                      style={{ fontFamily: '"Lora", serif' }}
                    >
                      {lastNameTranslated}
                    </Label>
                    <Input 
                      id="lastName" 
                      placeholder="Votre nom" 
                      className="mt-1 text-lg p-3" 
                      style={{ fontFamily: '"Lora", serif' }}
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label 
                    htmlFor="email"
                    className="text-lg"
                    style={{ fontFamily: '"Lora", serif' }}
                  >
                    {emailTranslated}
                  </Label>
                  <Input 
                    id="email" 
                    type="email" 
                    placeholder="votre.email@exemple.com" 
                    className="mt-1 text-lg p-3" 
                    style={{ fontFamily: '"Lora", serif' }}
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <Label 
                    htmlFor="subject"
                    className="text-lg"
                    style={{ fontFamily: '"Lora", serif' }}
                  >
                    {subjectTranslated}
                  </Label>
                  <Input 
                    id="subject" 
                    placeholder="Objet de votre message" 
                    className="mt-1 text-lg p-3" 
                    style={{ fontFamily: '"Lora", serif' }}
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                <div>
                  <Label 
                    htmlFor="message"
                    className="text-lg"
                    style={{ fontFamily: '"Lora", serif' }}
                  >
                    {messageTranslated}
                  </Label>
                  <Textarea 
                    id="message" 
                    placeholder="Votre message..." 
                    className="mt-1 min-h-40 text-lg p-3" 
                    style={{ fontFamily: '"Lora", serif' }}
                    value={formData.message}
                    onChange={handleChange}
                    required
                  />
                </div>
                
                {/* CAPTCHA simple */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <Label 
                    htmlFor="captcha"
                    className="text-lg font-semibold mb-2 block"
                    style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                  >
                    {captchaLabelTranslated}
                  </Label>
                  <div className="flex items-center gap-3">
                    <span className="text-xl font-bold" style={{ fontFamily: '"Lora", serif' }}>
                      {captchaQuestion.num1} + {captchaQuestion.num2} = ?
                    </span>
                    <Input 
                      id="captcha" 
                      type="number"
                      placeholder="?"
                      className="w-20 text-lg p-2 text-center"
                      style={{ fontFamily: '"Lora", serif' }}
                      value={captchaAnswer}
                      onChange={(e) => setCaptchaAnswer(e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit"
                  disabled={submitting}
                  className="w-full text-white hover:opacity-90 text-lg py-3"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    backgroundColor: 'rgb(196, 150, 96)'
                  }}
                >
                  {submitting ? sendingTranslated : sendButtonTranslated}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle 
                  className="text-3xl"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  {infoTitleTranslated}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: 'rgb(196, 150, 96)' }} />
                  <div>
                    <h3 
                      className="font-semibold text-gray-900 mb-1 text-xl"
                      style={{ fontFamily: '"Lora", serif' }}
                    >
                      {headquartersTranslated}
                    </h3>
                    <p 
                      className="text-gray-600 text-lg"
                      style={{ fontFamily: '"Lora", serif' }}
                    >
                      Galerie du Roi 5<br/>
                      1000 Bruxelles
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <MapPin className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: 'rgb(196, 150, 96)' }} />
                  <div>
                    <h3 
                      className="font-semibold text-gray-900 mb-1 text-xl"
                      style={{ fontFamily: '"Lora", serif' }}
                    >
                      {secretariatTranslated}
                    </h3>
                    <p 
                      className="text-gray-600 text-lg"
                      style={{ fontFamily: '"Lora", serif' }}
                    >
                      {postalAddressTranslated}<br/>
                      Avenue Bois du Dimanche 3<br/>
                      1150 Bruxelles
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <Mail className="w-6 h-6 mt-1 flex-shrink-0" style={{ color: 'rgb(196, 150, 96)' }} />
                  <div>
                    <h3 
                      className="font-semibold text-gray-900 mb-1 text-xl"
                      style={{ fontFamily: '"Lora", serif' }}
                    >
                      Email
                    </h3>
                    <a 
                      href="mailto:lignagesdebruxelles@gmail.com" 
                      className="hover:opacity-80 text-lg"
                      style={{ 
                        fontFamily: '"Lora", serif',
                        color: 'rgb(196, 150, 96)'
                      }}
                    >
                      lignagesdebruxelles@gmail.com
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-gray-200">
              <CardContent className="p-6">
                <h3 
                  className="text-2xl font-semibold mb-3"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  {becomeMemberTranslated}
                </h3>
                <p 
                  className="text-gray-700 text-lg mb-4"
                  style={{ fontFamily: '"Lora", serif' }}
                >
                  {interestedTranslated}
                </p>
                <Button 
                  variant="outline" 
                  className="hover:bg-opacity-10 text-lg py-2"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    borderColor: 'rgb(196, 150, 96)',
                    color: 'rgb(196, 150, 96)'
                  }}
                  onClick={() => window.location.href = '/admission'}
                >
                  {learnMoreTranslated}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Additional Information */}
        <div className="mt-12">
          <Card className="bg-gray-100">
            <CardContent className="p-8 text-center">
              <h3 
                className="text-3xl font-semibold text-gray-900 mb-4"
                style={{ fontFamily: '"Lora", serif' }}
              >
                {patronageTranslated}
              </h3>
              <p 
                className="text-gray-600 max-w-2xl mx-auto text-lg"
                style={{ fontFamily: '"Lora", serif' }}
              >
                {patronageDescTranslated}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
};

export default Contact;